import http from "./http.service";
import Promisable from "./promisable.service";
import { authActions } from "redux/slices/auth";
import { getAppDispatch } from "utils/dispatch.util";
import { bookingActions } from "./../redux/slices/booking";
import { MODAL, modalActions } from "redux/slices/modal";
import { buildGetPricesRequest } from "utils/aggregatorHourlyPricing.util";
import { store } from "redux/store";

const url = "/trip";

/* One shared flag drives the home search button and the bookings list.
   Canceling two bookings starts a details fetch and a list fetch that both
   flip that flag. A finished older fetch was turning it off while a newer
   one was still running, or turning it back on after the customer had
   already left for home, so the pickup search stayed on "Loading". Each
   call keeps its own id so only a call that is still in flight can hold it. */
const activeBookingLoads = new Set<number>();
let bookingLoadSeq = 0;

const beginBookingLoad = (dispatch: any) => {
  const id = ++bookingLoadSeq;
  activeBookingLoads.add(id);
  dispatch?.(bookingActions.setLoading(true));
  return id;
};

const endBookingLoad = (dispatch: any, id: number) => {
  activeBookingLoads.delete(id);
  dispatch?.(bookingActions.setLoading(activeBookingLoads.size > 0));
};

const BOOKINGS_FRESH_MS = 30000;
let bookingsRequest: Promise<any[]> | null = null;

export const releaseStaleBookingLoad = () => {
  activeBookingLoads.clear();
  getAppDispatch()?.(bookingActions.setLoading(false));
};

const BookingService = {
  verifyBooking: async (id: any) => {
    const [success, error]: any = await Promisable.asPromise(
      http.get(
        `/tripConfirmation?merchant_reference=${id}&is_verification=true&response_code=02000&service_command=VERIFICATION`
      )
    );
    return [success.data.data, error];
  },
  confirmPartnerStripePayment: async (
    tripId: string,
    paymentIntentId?: string,
    token?: string
  ) => {
    http.setJWT();
    http.acceptLanguage();
    const [success, error]: any = await Promisable.asPromise(
      http.post(
        `${url}/confirm-stripe-payment/${tripId}`,
        paymentIntentId ? { payment_intent_id: paymentIntentId } : {},
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : undefined
      )
    );
    return [success, error];
  },

  addBooking: async (data: any) => {
    const dispatch = getAppDispatch();
    const loadId = beginBookingLoad(dispatch);
    http.setJWT();
    http.acceptLanguage();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}`, data)
    );

    /* A new booking belongs in the list the next time it is opened. */
    if (success) dispatch?.(bookingActions.invalidateBookings());
    endBookingLoad(dispatch, loadId);
    return [success, error];
  },



  updateBooking: async (id: string, data: any, token: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();
    http.acceptLanguage();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/update/${id}`, data, {
        suppressSuccessToast: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    if (success) {
      const { trip } = success.data.data;

      dispatch?.(modalActions.closeModal());
      dispatch?.(bookingActions.updateBooking(trip));
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  confirmBooking: async (data: any) => {
    const dispatch = getAppDispatch();
    const loadId = beginBookingLoad(dispatch);
    http.acceptLanguage();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/confirmtrip`, data)
    );

    endBookingLoad(dispatch, loadId);
    return [success, error];
  },

  cancelBooking: async (id: any, token: string, timeFormat?: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));
    http.acceptLanguage();

    const [success, error]: any = await Promisable.asPromise(
      http.delete(`${url}/${id}`, {
        suppressSuccessToast: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );

    /* The confirmation answers a tap that may be seconds old. If the reader
       has since left the booking, or opened something else (say Edit while
       this was loading), it must not appear over that — it used to replace
       a half-filled edit form with "Confirm cancellation? Yes / No". Only
       the cancel dialog this request came from may be swapped. */
    const modal = store.getState().modal;
    const stillHere =
      window.location.pathname.includes(`/booking/${id}`) &&
      (!modal.open ||
        (modal.type === MODAL.CONFIRMATION_FORM &&
          modal.data?.type === MODAL.CANCEL_BOOKING &&
          modal.data?.id === id));

    if (success && stillHere) {
      const { trip, message, is_refund, refund } = success.data.data;

      dispatch?.(modalActions.closeModal());

      if (trip) dispatch?.(bookingActions.updateBooking(trip));

      if (!is_refund)
        dispatch?.(
          modalActions.openModal({
            width: "520px",
            type: MODAL.CONFIRMATION_FORM,
            data: {
              id,
              message,
              type: MODAL.CONFIRM_CANCEL_BOOKING,
              heading: refund
                ? "cancel_dialog_title"
                : "confirm_booking_cancellation",
              token,
              /* What the dialog lays out (refund, amount, deadline). An
                 older backend sends only `message`, shown as before. */
              refund,
              timeFormat,
            },
          })
        );
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  confirmCancelBooking: async (id: any, token: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));
    http.acceptLanguage();
    const [success, error]: any = await Promisable.asPromise(
      http.delete(`${url}/confirmcancel/${id}`, {
        suppressSuccessToast: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );

    if (success) {
      const { trip } = success.data.data;

      if (trip) dispatch?.(bookingActions.updateBooking(trip));
      /* The reply may carry no trip; the list is out of date either way. */
      dispatch?.(bookingActions.invalidateBookings());

      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  /* The bookings tab asks for the list on every visit. A list that arrived
     in the last BOOKINGS_FRESH_MS (and has not been invalidated by a cancel,
     an edit or a new booking) is shown as is; otherwise it is fetched while
     the cached one stays on screen. Visits that overlap share one request,
     so quick tab switching no longer fires a request per tap. */
  getAllBookings: async ({ force = false }: { force?: boolean } = {}) => {
    const fetchedAt = store.getState().booking.bookingsFetchedAt;
    if (!force && fetchedAt && Date.now() - fetchedAt < BOOKINGS_FRESH_MS) {
      return [null, null];
    }
    if (bookingsRequest) return bookingsRequest;

    const dispatch = getAppDispatch();
    dispatch?.(bookingActions.setListStatus("loading"));

    http.setJWT();
    http.acceptLanguage();

    bookingsRequest = (async () => {
      const [success, error]: any = await Promisable.asPromise(
        http.get(`${url}`)
      );

      if (success) {
        const { trips } = success.data.data;
        dispatch?.(bookingActions.setBookings(trips));
      } else {
        dispatch?.(bookingActions.setListStatus("failed"));
      }
      bookingsRequest = null;
      return [success, error];
    })();

    return bookingsRequest;
  },

  processPartnerPayment: async (tripId: string) => {
    http.setJWT();
    http.acceptLanguage();
    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/payment-process/${tripId}`)
    );
    return [success?.data?.data, error];
  },

  getBookingPrices: async (data: any) => {
    const dispatch = getAppDispatch();
    const loadId = beginBookingLoad(dispatch);
    http.setJWT();
    http.acceptLanguage();
    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/getprices`, buildGetPricesRequest(data))
    );
    endBookingLoad(dispatch, loadId);
    return [success, error];
  },

  getBookingById: async (id: any, token: any) => {
    const dispatch = getAppDispatch();

    http.setJWT();
    http.acceptLanguage();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/get/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(bookingActions.setBooking(trip));
    }

    return [success, error];
  },


  tripRating: async (data: any) => {
    const dispatch = getAppDispatch();

    http.setJWT();
    http.acceptLanguage();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/rating`, data)
    );

    if (success) dispatch?.(authActions.setUserTripRating(true));

    return [success, error];
  },

  confirmTip: async (data: any) => {
    http.setJWT();
    http.acceptLanguage();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/confirmTip`, data)
    );

    return [success, error];
  },

  reportProblem: async (token: string, id: string, data: any) => {
    const dispatch = getAppDispatch();
    http.setJWT();
    http.acceptLanguage();
    dispatch?.(modalActions.setLoading(true));

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/report/${id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
    );

    if (success) {
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  requestHistory: async (_data: any) => {
    // const response = await fetch("https://geolocation-db.com/json/");
    // const res = await response.json();
    // payload.userID = data.user?._id;
    // payload.pickup = `${data.name} ${data.formatted_address}`;
    // payload.city = data.city;
    // payload.lat = data.geometry.location.lat;
    // payload.lng = data.geometry.location.lng;
    // payload.iP = res?.IPv4;
    // http.acceptLanguage();

    // const [success, error]: any = await Promisable.asPromise(
    //   http.post("/request-history", payload)
    // );
    // return [success, error];
  },

  bookingHistory: async (data: any) => {
    // let payload = { ...data };
    // let prices: any = [];
    // data?.classes &&
    //   data?.classes.forEach((element: any) => {
    //     prices.push({
    //       class: element.class_name,
    //       price: element.price_details.netto,
    //     });
    //   });
    // payload.prices = prices;
    // delete payload.classes;
    // const response = await fetch("https://geolocation-db.com/json/");
    // const res = await response.json();
    // payload.pickup = data.pickup;
    // payload.destination = data?.destination ? data?.destination : data?.hours;
    // payload.iP = res.IPv4;
    // http.acceptLanguage();

    // const [success, error]: any = await Promisable.asPromise(
    //   http.post("/request-history", payload)
    // );
    // return [success, error];
  },
};

export default BookingService;
